/*
 * Описание формы блока в JSON и правила его полей (004 T016; 002 T056/T139; contracts/step-contract.md §2: «форма
 * блока — порядок полей, видимость по `showWhen`, блокировка по `law`, обязательность по `noDefault`»).
 *
 * Описание строит `meta/to-form` движка (`FormSpec`) и отдаёт сервер. Интерфейс — клиент HTTP API и пакет движка не
 * импортирует, поэтому типы и правила полей для него — здесь: одни для формы правила (`ParamForm`), трассы расчёта
 * (`TraceTable`) и страницы правил 004 (research R23). Правила те же, что `fieldVisible`, `formModes`,
 * `fieldRequired` в `meta/to-form.ts`; совпадение на формах всех правил проверяет
 * `packages/engine/tests/meta/form-fields-shared.test.ts`.
 */

/** Элемент управления поля (`FormControl`). */
export type FormControlView =
  | 'select'
  | 'multiselect'
  | 'integer'
  | 'number'
  | 'text'
  | 'boolean'
  | 'map'
  | 'list'
  | 'json';

/** Вариант значения: значение, его описание (`enumDocs`), у списка кубиков — их типы пометок. */
export interface FormOptionView {
  readonly value: string | number | boolean;
  readonly doc?: string;
  readonly flags?: readonly string[];
}

/** Строка словаря по типам пометок: тип шага задаётся, тип ядра и сервиса постоянен (`fixed`). */
export interface FormKeyView {
  readonly code: string;
  readonly source: string;
  readonly editable: boolean;
  readonly cube?: string;
  readonly fixed?: unknown;
  /** Простое название пометки (004, `PLAIN_FLAGS` движка): строка таблицы для заказчика. */
  readonly label?: string;
}

/** Поле формы (`FormField`). */
export interface FormFieldView {
  readonly name: string;
  readonly label: string;
  readonly hint?: string;
  readonly control: FormControlView;
  readonly required: boolean;
  readonly options?: readonly FormOptionView[];
  readonly keys?: readonly FormKeyView[];
  readonly min?: number;
  readonly max?: number;
  readonly pattern?: string;
  readonly default?: unknown;
  readonly examples?: readonly unknown[];
  readonly unit?: string;
  readonly timeKind?: string;
  readonly inclusive?: boolean;
  readonly noDefault?: { readonly readWhenModes: readonly string[] };
  /** Параметр режима блока (`ParamMeta.mode`, Р-93). */
  readonly mode?: true;
  readonly showWhen?: { readonly field: string; readonly in: readonly unknown[] };
  readonly locked?: { readonly value: unknown; readonly article: string };
  readonly bundle?: string;
  readonly cardRef?: string;
  readonly questionRefs?: readonly string[];
  readonly personalData?: boolean;
}

/** Описание формы (`FormSpec`). */
export interface FormSpecView {
  readonly cube: string;
  readonly kind: string;
  readonly title: string;
  readonly fields: readonly FormFieldView[];
  /** Параметр режима блока: режим — его значение в `params` (Р-93). */
  readonly modeField?: string;
  /** Пометки с отметкой «блокирует» вне списка (Q5); подпись по этой отметке сервис табелей не держит (поправка FR-043 от 17.10.2026, Q-31). */
  readonly alwaysBlocking?: readonly string[];
}

/** Поле видно при этих значениях блока: `showWhen` нет — видно всегда. */
export function fieldVisible(field: FormFieldView, values: Readonly<Record<string, unknown>>): boolean {
  if (!field.showWhen) return true;
  return field.showWhen.in.includes(values[field.showWhen.field]);
}

/** Режимы блока: значение параметра режима (`modeField`) в `values`; строка — один, множество — его строки. */
export function formModes(spec: FormSpecView, values: Readonly<Record<string, unknown>>): string[] {
  const v = spec.modeField === undefined ? undefined : values[spec.modeField];
  if (typeof v === 'string') return [v];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/**
 * Поле обязательно при режиме блока: «умолчания нет» — в режимах `readWhenModes` (режим-множество — если в нём есть
 * такое значение), пустой список — всегда; без `noDefault` — как требует схема. У функции ядра режима нет — `null`.
 */
export function fieldRequired(field: FormFieldView, mode: string | readonly string[] | null): boolean {
  if (field.noDefault) {
    const read = field.noDefault.readWhenModes;
    const modes = mode === null ? [] : typeof mode === 'string' ? [mode] : mode;
    return read.length === 0 || modes.some((m) => read.includes(m));
  }
  return field.required;
}

/** Значение скрыто сервером (данные людей, `personalData`): `{ hidden: true }`. */
export function isHidden(v: unknown): boolean {
  return typeof v === 'object' && v !== null && (v as { hidden?: unknown }).hidden === true;
}

/** Значение для показа: строка, число, да/нет, список через запятую, остальное — JSON. */
export function showValue(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(showValue).join(', ');
  return JSON.stringify(v);
}
